<script setup>
import { computed } from 'vue';
const props = defineProps({
  title: { type: String, default: '' },
  chartData: { type: Array, required: true },
  viewType: { type: String, default: 'school' } 
});

const stepSize = computed(() => props.viewType === 'school' ? 20 : 10);
const yTicks = computed(() => {
  const steps = [];
  for (let i = 0; i <= 100; i += stepSize.value) {
    steps.push(i);
  }
  return steps;
});

const legend = [
  { label: '남자', color: '#0F7EF1' },
  { label: '여자', color: '#F895D1' },
  { label: '전체', color: '#C0CCDB' }
];
</script>

<template>
  <div class="chart-container" :class="`chart-${props.viewType}`">
    <div class="legend">
      <div v-for="item in legend" :key="item.label" class="legend-item">
        <span class="color-box" :style="{ backgroundColor: item.color }"></span>
        <span class="label-text">{{ item.label }}</span>
      </div>
    </div>
    <div class="chart-wrapper">
      <div class="y-axis">
        <span v-for="tick in yTicks" :key="tick">{{ tick }}%</span>
      </div>
      <div class="chart-content">
        <div class="grid-lines">
          <div v-for="tick in yTicks" :key="tick" class="grid-line"></div>
        </div>
        <div class="bar-groups">
          <div v-for="group in props.chartData" :key="group.level" class="bar-group">
            <div class="bars">
              <div class="bar male" :style="{ height: group.male + '%' }">
                <span class="value">{{ group.male }}</span>
              </div>
              <div class="bar female" :style="{ height: group.female + '%' }">
                <span class="value">{{ group.female }}</span>
              </div>
              <div class="bar total" :style="{ height: group.total + '%' }">
                <span class="value">{{ group.total }}</span>
              </div>
            </div>    
            <div class="x-axis-label">{{ group.level }}</div>        
          </div>
        </div>
      </div>      
    </div>     
  </div>
</template>

<style lang="scss" scoped>
@use "sass:list";
$ai-levels: (
  var(--result-ai-primary-90),
  var(--result-ai-primary-80),  
  var(--result-ai-primary-70),
  var(--result-ai-primary-60), 
);
.chart-container {
  width: 100%;
  padding: 0 0 25px;
  margin: 0 auto;

  .chart-wrapper {
    display: flex;
    width: 100%;
    height: 220px;
    position: relative;

    .y-axis {
      display: flex;
      flex-direction: column-reverse;
      justify-content: space-between;
      align-items: flex-end;
      padding-right: 12px;
      width: 50px;
      font-size: 1rem;
      color: var(--result-ai-primary-800);
      line-height: 0;
      font-weight: 500;
    }
  }
  .chart-content {
    position: relative;  
    width: calc(100% - 50px);
    .grid-lines {
      position: absolute;
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      .grid-line {
        border-bottom: 1px dashed #e0e0e0; 
        &:last-child {border: 0;}
      }
    } 
    .bar-groups {
      position: absolute;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: flex-end;     
      border-bottom: 2px solid var(--result-ai-border);
      .bar-group {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 25%;
        height: 100%;
        .bars {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;         
          width: 100%;
          height: 100%;
          padding: 0 2rem;
          position: relative;
          .bar {
            position: relative;
            width: 20px;
            border-radius: 4px 4px 0 0;        
            &.male { background-color: #0F7EF1; }
            &.female { background-color: #F895D1; }
            &.total { background-color: #C0CCDB; }
            .value {
              position: absolute;
              top: -22px;
              left: 50%;
              transform: translateX(-50%);
              font-size: 16px;
              white-space: nowrap;
              font-weight: 600;
            }
          }
          &::after {
            content: "";
            position: absolute;
            width: 2px;
            height: 105%;
            right: 0;
            bottom: 0;
            background-color: var( --smoe-gray-300);
          }         
        } 
        .x-axis-label {
          font-size: 16px;
          position: absolute;
          font-weight: 600;
          bottom: -30px;
          width: 180px;
          border-radius: 10px;
          text-align: center;
        }        
        &:last-child .bars::after { display: none; }
        @for $i from 1 through 4 {          
          &:nth-child(#{$i}) .x-axis-label {              
            background-color: list.nth($ai-levels, $i);  
          }            
        }   
      }      
    }    
  }
  &.chart-class .chart-wrapper { height: 320px; }
}
.legend {
  display: flex;
  justify-content: flex-end;
  color: var(--result-ai-primary-800);
  font-weight: 600;
  font-size: 16px;
  gap: 1rem;
  padding-bottom: 1rem;
  .legend-item {
    display: flex;
    align-items: center;
    gap: .5rem;
    .color-box { width: .563rem; height: .563rem; border-radius: 50%; }    
  }
}
</style>